import React from "react";
import clsx from "clsx";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import AppsIcon from "@mui/icons-material/Apps";
import { useHistory, useLocation } from "react-router-dom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useDispatch, useSelector } from "react-redux";
import { Breadcrumbs, FormControl, MenuItem, Tooltip, Typography } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import { InputLabel } from "@mui/material";
import { Select } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { openSidebar } from "../store/ducks/duckSidebar";
import { useStylesLayout } from "../styles/Layout";

import { LinkRouter } from "./PathWay";
import LoadingComponent from "./LoadingComponent";

const theme = createTheme({
  palette: {
    primary: {
      main: "#fff",
    },
    type: "dark",
    action: {
      selected: "#fff",
      hover: "#fff",
      disabled: "#fff",
    },
    background: {
      default: "#fff",
    },
    text: {
      primary: "#fff",
      secondary: "#fff",
      disabled: "#fff",
    },
  },
  overrides: {
    MuiMenuItem: {
      root: {
        "&$selected": {
          backgroundColor: "rgba(76, 175, 80, 0.6)",
          fontWeight: "bolder",
        },
        color: "rgba(0, 0, 0, 0.87)",
        backgroundColor: "#fff",
      },
    },
    MuiSelect: {
      root: {
        borderColor: "white",
        color: "white",
      },
      outlined: {
        borderColor: "white",
        color: "white",
      },
      icon: {
        color: "white",
      },
    },
  },
});

const NavBar = (props) => {
  const {
    fiscalYears = [],
    setFiscalYear = () => {},
    withLocation = false,
    isHr = false,
    fiscalYear = "",
  } = props;
  const classes = useStylesLayout();
  const history = useHistory();
  const sidebarOpen = useSelector((state) => state.sidebar.open);
  const dispatch = useDispatch();

  const handleSidebarOpen = () => dispatch(openSidebar());

  const { pathname } = useLocation();

  const handleNavBarClick = (e) => {
    e.preventDefault();

    if (pathname !== "/") {
      history.push("/");
    }
  };

  const toggleFullScreen = (elem) => {
    if (
      (document.fullScreenElement !== undefined && document.fullScreenElement === null) ||
      (document.msFullscreenElement !== undefined && document.msFullscreenElement === null) ||
      (document.mozFullScreen !== undefined && !document.mozFullScreen) ||
      (document.webkitIsFullScreen !== undefined && !document.webkitIsFullScreen)
    ) {
      if (elem.requestFullScreen) {
        elem.requestFullScreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullScreen) {
        elem.webkitRequestFullScreen(Element.ALLOW_KEYBOARD_INPUT);
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } else {
      if (document.cancelFullScreen) {
        document.cancelFullScreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  return (
    <AppBar
      className={clsx(classes.appBar, {
        [classes.appBarShift]: sidebarOpen,
      })}
      position="fixed"
    >
      <Toolbar variant="dense">
        <Grid container item alignItems="center" direction="row" justifyContent="flex-start" xs={6}>
          <Grid item xs={1}>
            <IconButton
              aria-label="sidebarOpen drawer"
              className={clsx(classes.menuButton, {
                [classes.hide]: sidebarOpen,
              })}
              color="default"
              edge="start"
              sx={{
                backgroundColor: "transparent",
                color: "white",
                border: "none",
                outline: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "&:focus": {
                  outline: "none",
                },
                "&:focus-visible": {
                  outline: "none",
                },
              }}
              onClick={handleSidebarOpen}
            >
              <AppsIcon data-cy="open-sidebar" sx={{ color: "#fff" }} />
            </IconButton>
          </Grid>
          <Grid item xs={10}>
            {withLocation ? (
              <Breadcrumbs aria-label="breadcrumb" style={{ color: "white" }}>
                <LinkRouter color="inherit" style={{ color: "#fff", textShadow: "none" }} to="/">
                  Home
                </LinkRouter>
                <Typography
                  key={`BreadCrumb-CurrentLocation`}
                  style={{ color: "#fff", textShadow: "none" }}
                >
                  {pathname && pathname.substr(1)}
                </Typography>
              </Breadcrumbs>
            ) : (
              <img
                alt="logo"
                height={34}
                src={`${process.env.PUBLIC_URL}/ReportsNavbar.png`}
                width={101}
                onClick={handleNavBarClick}
              />
            )}
          </Grid>
        </Grid>
        <Grid
          container
          item
          alignItems="center"
          direction="row-reverse"
          justifyContent="flex-start"
          style={{
            float: "right",
            borderColor: "white",
          }}
          xs={6}
        >
          <Tooltip className={pathname !== "/" ? classes.iconHelp : undefined} title="FullScreen">
            <IconButton
              aria-label="FullScreen"
              color="inherit"
              style={{ marginLeft: 30 }}
              onClick={() => toggleFullScreen(document.body)}
            >
              <FullscreenIcon />
            </IconButton>
          </Tooltip>
          {pathname === "/" && (
            <Tooltip title="Help">
              <IconButton
                aria-label="sidebarOpen drawer"
                className={classes.iconHelp}
                color="default"
                edge="end"
                href={"https://techintingenieria.sharepoint.com/sites/tda/SitePages/Reports.aspx"}
                target="_blank"
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
          {isHr && fiscalYears.length > 0 ? (
            <ThemeProvider theme={theme}>
              <Grid item>
                <FormControl style={{ borderColor: "white" }} variant="outlined">
                  <InputLabel id="demo-simple-select-outlined-label">Fiscal Year</InputLabel>
                  <Select
                    id="demo-simple-select-outlined"
                    label="Fiscal Year"
                    labelId="demo-simple-select-outlined-label"
                    style={{ color: "white", height: 30 }}
                    value={fiscalYear}
                    onChange={(e) => setFiscalYear(e.target.value)}
                  >
                    {fiscalYears.length > 0 ? (
                      fiscalYears.map((x) => {
                        return (
                          <MenuItem key={`$-${x}`} aria-valuetext={x} value={x}>
                            {x}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem key={`$-${"as"}`} aria-valuetext={"test"} value={"test"}>
                        test
                      </MenuItem>
                    )}
                    {/* <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value={10}>Ten</MenuItem>
                <MenuItem value={20}>Twenty</MenuItem>
                <MenuItem value={30}>Thirty</MenuItem> */}
                  </Select>
                </FormControl>
              </Grid>
            </ThemeProvider>
          ) : (
            []
          )}
        </Grid>
      </Toolbar>
    </AppBar>
  );
};

NavBar.propTypes = {
  withLocation: PropTypes.bool,
  isHr: PropTypes.bool,
  setFiscalYear: PropTypes.func,
  fiscalYears: PropTypes.array,
};

export default NavBar;
